import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { useModalStore } from '@/Domain/store/modal.store';

export const campaignSchema = z.object({
  id: z.number(),
  name: z
    .string()
    .min(1, { message: i18n.t('campaigns:form.validation.name_required') })
    .max(100, { message: i18n.t('campaigns:form.validation.max_length', { max: 100 }) }),
  description: z
    .string()
    .max(500, { message: i18n.t('campaigns:form.validation.max_length', { max: 500 }) }),
  reason_for_gathering: z
    .string()
    .min(1, { message: i18n.t('campaigns:form.validation.reason_for_collecting_required') })
    .max(250, { message: i18n.t('campaigns:form.validation.max_length', { max: 250 }) }),
  question_timing: z.literal('immediate').or(z.literal('short_delay')),
  question_type: z.literal('subtle').or(z.literal('direct')),
  is_enabled: z.boolean(),
  parameters: z.array(
    z.object({
      id: z.number().optional(),
      name: z
        .string()
        .min(1, { message: i18n.t('campaigns:form.validation.parameter_name_required') })
        .max(100, { message: i18n.t('campaigns:form.validation.max_length', { max: 100 }) }),
      description: z
        .string()
        .min(1, { message: i18n.t('campaigns:form.validation.parameter_description_required') })
        .max(500, { message: i18n.t('campaigns:form.validation.max_length', { max: 500 }) }),
      data_format: z
        .string()
        .min(1, { message: i18n.t('campaigns:form.validation.parameter_data_format') })
        .max(500, { message: i18n.t('campaigns:form.validation.max_length', { max: 500 }) }),
      is_enabled: z.boolean(),
    }),
  ),
});

export type CampaignForm = z.infer<typeof campaignSchema>;
export const useCampaignActionForm = (data: CampaignDto) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('campaigns');
  const { open, closeModal } = useModalStore();

  const mutation = useMutation<CampaignDto, AxiosError<ErrorResponse>, CampaignForm>({
    mutationKey: [QueryKey.campaign.update],
    onSuccess: (campaign) => {
      toast.success(t('toast.update_successfully'));
      queryClient.setQueryData([QueryKey.campaign.get, data.id], () => campaign);
      queryClient.setQueryData([QueryKey.campaign.list], (prev: CampaignDto[] = []) =>
        prev.map((prevCampaign) =>
          prevCampaign.id === campaign.id
            ? { ...prevCampaign, name: campaign.name, description: campaign.description }
            : prevCampaign,
        ),
      );

      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.errors?.root?.[0]?.message || t('toast.update_failed'));
    },
  });

  const form = useForm<CampaignForm>({
    defaultValues: {
      id: data?.id,
      name: data?.name || '',
      description: data?.description || '',
      reason_for_gathering: data?.reason_for_gathering || '',
      question_timing: data?.question_timing || 'immediate',
      question_type: data?.question_type || 'subtle',
      is_enabled: data?.is_enabled || false,
      parameters: data?.parameters || [],
    },
    resolver: zodResolver(campaignSchema),
  });

  useEffect(() => {
    if (data) {
      form.reset({
        id: data?.id,
        name: data?.name || '',
        description: data?.description || '',
        reason_for_gathering: data?.reason_for_gathering || '',
        question_timing: data?.question_timing || 'immediate',
        question_type: data?.question_type || 'subtle',
        is_enabled: data?.is_enabled || false,
        parameters: data?.parameters || [],
      });
    }
  }, [open, data]);

  const handleSubmit = (data: CampaignForm) => mutation.mutate(data);

  return { form, handleSubmit };
};
